import { Skeleton, Switch, Tabs, Tooltip } from "antd"
import buildingIcon from "assets/svg/Buildings.svg"
import userIcon from "assets/svg/users.svg"
import shiftIcon from "assets/svg/zero-hexagon.svg"
import reportingIcon from "assets/svg/chart-bar-two.svg"
import { useState } from "react"
import Reporting from "./ReportingWorkplace"
import DutyLocationWorkplace from "./DutyLocationWorkplace"
import { useParams, useSearchParams } from "react-router-dom"
import { useGet } from "utils/useFetch"
import { IWorkPlacesEntity, OfficeDutyLocationsEntity } from "../typings"
import SelectDutyLocationWrapper, {
  DutyLocationItem,
} from "../components/SelectDutyLocationWrapper"
import CustomTAndAWrapper from "../components/CustomTAndAWrapper"
import ConfigurationWorkplace from "./ConfigurationWorkplace"
import EnrolledEmployeeWorkplace from "./EnrolledEmployeeWorkplace"
import { getStaticMapUrl } from "components/GeoPicker"

// ─── Constants ────────────────────────────────────────────────────────────────

const COVER_IMAGE =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/2733ead3-665e-4524-ab4e-64a22f9a21aa.png"

// ─── Types ────────────────────────────────────────────────────────────────────

export type WorkplaceRes = IBaseRes<IWorkPlacesEntity>

// ─── Sub-components ───────────────────────────────────────────────────────────

const TabLabel = ({ icon, label }: { icon: string; label: string }) => (
  <div className="flex items-center gap-1 justify-center">
    <img
      width={15}
      height={15}
      src={icon}
      alt={label}
    />
    <span className="tab-text text-sm">{label}</span>
  </div>
)

// ─── Main Component ───────────────────────────────────────────────────────────

const ViewWorkPlace = () => {
  const { x } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: workplace, isLoading } = useGet<WorkplaceRes>({
    url: `/v1/time-attendance/workplaces/${x}`,
    instanceKey: "ewaAxiosInstance",
  })

  const initialSelectedLocation = (() => {
    const raw = searchParams.get("dutyLocationId")
    if (!raw) return "workplace" as const
    const n = Number(raw)
    return Number.isFinite(n) ? n : ("workplace" as const)
  })()

  const [selectedLocation, setSelectedLocation] = useState<
    number | "workplace"
  >(initialSelectedLocation)

  const [activeTabKey, setActiveTabKey] = useState("1")

  const hasActiveShiftPlan = !!workplace?.data?.shiftPlans?.some(
    (sp) => sp?.isActive && !sp?.isDeleted && sp?.status === "published"
  )

  const handleSetSelectedLocation = (value: number | "workplace") => {
    setSelectedLocation(value)
    const next = new URLSearchParams(searchParams)
    if (value === "workplace") next.delete("dutyLocationId")
    else next.set("dutyLocationId", String(value))
    setSearchParams(next, { replace: true })
  }

  const activeLocation =
    selectedLocation || workplace?.data?.officeDutyLocations || ""
  const dutyLocationId = activeLocation

  const isPublished = workplace?.data?.workplaceConfig?.status === "published"

  const dutyLocationOptions = [
    {
      id: "workplace",
      name: workplace?.data?.displayName || "",
      addressLine1: workplace?.data?.fullAddress || "",
      longitude: workplace?.data?.longitude,
      latitude: workplace?.data?.latitude,
    },
    ...(workplace?.data?.officeDutyLocations?.map((i) => {
      return {
        id: i?.id,
        name: i?.name,
        addressLine1: i?.addressLine1 ?? "",
        // prefer the duty location's own coordinates; fall back to workplace coords
        longitude: i?.longitude ?? workplace?.data?.longitude,
        latitude: i?.latitude ?? workplace?.data?.latitude,
      }
    }) || []),
  ]

  const tabItems = [
    {
      key: "1",
      label: (
        <TabLabel
          icon={reportingIcon}
          label="Reporting"
        />
      ),
      children: <Reporting />,
    },
    {
      key: "2",
      label: (
        <TabLabel
          icon={shiftIcon}
          label="Configuration"
        />
      ),
      children: (
        <ConfigurationWorkplace
          workplaceId={x}
          dutylocationId={dutyLocationId as number | "workplace"}
        />
      ),
    },
    {
      key: "3",
      label: (
        <TabLabel
          icon={buildingIcon}
          label="Duty locations"
        />
      ),
      children: (
        <DutyLocationWorkplace
          workplace={workplace}
          isLoading={isLoading}
        />
      ),
    },
    {
      key: "4",
      label: (
        <TabLabel
          icon={userIcon}
          label="Enrolled employees"
        />
      ),
      children: (
        <EnrolledEmployeeWorkplace
          hasActiveShiftPlan={hasActiveShiftPlan}
          onViewShiftPlan={() => setActiveTabKey("2")}
        />
      ),
    },
  ]

  const mapLocation = dutyLocationOptions.find(
    (location) => location.id === activeLocation
  )

  return (
    <CustomTAndAWrapper>
      <div className="bg-white overflow-hidden relative">
        {/* Cover Image */}
        <div />
        <div className="relative w-full h-52 overflow-hidden">
          {/* Map Image Element */}
          <div
            className="w-full h-full bg-center bg-no-repeat bg-cover"
            style={{
              /* Cleaned up string quotes inside url() syntax */
              backgroundImage: `url(${getStaticMapUrl(
                Number(mapLocation?.latitude!),
                Number(mapLocation?.longitude!)
              )})`,
            }}
          />
          {/* Bottom Fade Overlay - Explicitly pinned to the map's container */}
          <div className="absolute inset-x-0 bottom-0 h-16 pointer-events-none bg-gradient-to-t from-white to-transparent z-10" />
        </div>
        <div className="px-6 pb-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-2">
            <div>
              {isLoading ? (
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 1, width: "100%" }}
                />
              ) : (
                <h5 className="font-bold capitalize text-3xl text-slate-900">
                  {workplace?.data?.displayName}
                </h5>
              )}
              <p className="text-slate-500 text-sm pt-1">
                Track attendance, manage shift schedules, review swap requests,
                and configure duty locations.
              </p>
            </div>

            {/* Status Toggle */}
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold">
                Status:{" "}
                <strong className="text-[#004AF5]">
                  {isPublished ? "Published" : "Draft"}
                </strong>
              </p>
              <Tooltip title="Enable or disable attendance tracking for this workplace">
                <Switch
                  checked={isPublished}
                  style={{
                    backgroundColor: isPublished ? "#004AF5" : "#E2E8F0",
                  }}
                />
              </Tooltip>
            </div>
          </div>

          {/* Duty Location Selector */}
          <SelectDutyLocationWrapper
            dutyLocation={dutyLocationOptions as DutyLocationItem[]}
            isLoadingDutyLocation={isLoading}
            selectedLocation={activeLocation as number | "workplace"}
            setSelectedLocation={handleSetSelectedLocation}
          />

          {/* Tabs */}
          <Tabs
            activeKey={activeTabKey}
            onChange={setActiveTabKey}
            className="my-5 [&_.tab-text]:text-slate-500 [&_.ant-tabs-tab-active_.tab-text]:!text-slate-900 [&_.ant-tabs-ink-bar]:!bg-slate-900"
            items={tabItems}
          />
        </div>
      </div>
    </CustomTAndAWrapper>
  )
}

export default ViewWorkPlace
